import { Dispatch } from 'redux'
import { api, apiErrorHandler, getApiRequestConfig, Response } from '../../api'
import { GetState } from '../reducers'
import { Favourite } from '../reducers/favourite.reducer'

export type FavouriteProductType = 'food' | 'event' | 'appointment'

export enum FavouriteActionTypes {
    ADD_FAVOURITE = 'ADD_FAVOURITE',
    REMOVE_FAVOURITE = 'REMOVE_FAVOURITE',
    SET_FAVOURITES = 'SET_FAVOURITES',
    CLEAR_FAVOURITES = 'CLEAR_FAVOURITES'
}

export interface AddFavouriteAction {
    type: FavouriteActionTypes.ADD_FAVOURITE
    data: Favourite
}

export interface RemoveFavouriteAction {
    type: FavouriteActionTypes.REMOVE_FAVOURITE
    data: Favourite
}

export interface SetFavouritesAction {
    type: FavouriteActionTypes.SET_FAVOURITES
    data: Favourite[]
}

export interface ClearFavouritesAction {
    type: FavouriteActionTypes.CLEAR_FAVOURITES
}

export type FavouriteActions =
    | AddFavouriteAction
    | RemoveFavouriteAction
    | SetFavouritesAction
    | ClearFavouritesAction

export const addFavourite = (input: Favourite): AddFavouriteAction => {
    return {
        type: FavouriteActionTypes.ADD_FAVOURITE,
        data: input
    }
}

export const removeFavourite = (input: Favourite): RemoveFavouriteAction => {
    return {
        type: FavouriteActionTypes.REMOVE_FAVOURITE,
        data: input
    }
}

export const setFavourites = (input: Favourite[]): SetFavouritesAction => {
    return {
        type: FavouriteActionTypes.SET_FAVOURITES,
        data: input
    }
}

export const clearFavourites = (): ClearFavouritesAction => {
    return {
        type: FavouriteActionTypes.CLEAR_FAVOURITES
    }
}

const isItemFavourite = (favourites: Favourite[], shopId: string, itemId: string) => {
    return favourites.some(fav => fav.type === 'item' && fav.shopId === shopId && fav.itemId === itemId)
}

const isShopFavourite = (favourites: Favourite[], shopId: string) => {
    return favourites.some(fav => fav.type === 'shop' && fav.shopId === shopId)
}

export const fetchUserFavourites = () => async (dispatch: Dispatch, getState: GetState) => {
    const authUser = getState().authUser

    if (!authUser) {
        return
    }

    const config = getApiRequestConfig(authUser['auth-token'])

    return api.get<Response<Favourite[]>>('user/favourites', config).then(response => {
        if (response.status === 200) {
            dispatch(setFavourites(response.data.data))
        } else {
            throw { response }
        }
    }).catch((error) => {
        return Promise.reject<string>(apiErrorHandler(error, 'Unable to fetch favourites'))
    })
}

export const toggleItemFavourite = (input: Favourite, productType: FavouriteProductType) => {
    return async (dispatch: Dispatch, getState: GetState) => {
        if (!input.itemId) {
            return
        }

        const authUser = getState().authUser
        const favourites = getState().favourites
        const currentlyFavourite = isItemFavourite(favourites, input.shopId, input.itemId)
        const previousFavourites = favourites

        if (currentlyFavourite) {
            dispatch(removeFavourite(input))
        } else {
            dispatch(addFavourite(input))
        }

        if (!authUser) {
            return
        }

        const config = getApiRequestConfig(authUser['auth-token'])
        const endpoint = `${productType}/${input.itemId}/favourite`

        try {
            if (currentlyFavourite) {
                await api.delete(endpoint, config)
            } else {
                await api.post(endpoint, {}, config)
            }
        } catch {
            dispatch(setFavourites(previousFavourites))
        }
    }
}

export const toggleShopFavourite = (shopId: string) => {
    return async (dispatch: Dispatch, getState: GetState) => {
        const authUser = getState().authUser
        const favourites = getState().favourites
        const input: Favourite = { shopId, type: 'shop' }
        const currentlyFavourite = isShopFavourite(favourites, shopId)
        const previousFavourites = favourites

        if (currentlyFavourite) {
            dispatch(removeFavourite(input))
        } else {
            dispatch(addFavourite(input))
        }

        if (!authUser) {
            return
        }

        const config = getApiRequestConfig(authUser['auth-token'])
        const endpoint = `shop/${shopId}/favourite`

        try {
            if (currentlyFavourite) {
                await api.delete(endpoint, config)
            } else {
                await api.post(endpoint, {}, config)
            }
        } catch {
            dispatch(setFavourites(previousFavourites))
        }
    }
}
